import uuid from 'uuid';
import React, {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import type { FC, ReactNode } from 'react';

import type { EditorActions } from '@atlaskit/editor-core/actions';
import type {
	InlineCommentCreateComponentProps as EditorInlineCommentCreateComponentProps,
	InlineCommentViewComponentProps as EditorInlineCommentViewComponentProps,
} from '@atlaskit/editor-plugins/annotation';
import {
	type ActionResult,
	type AnnotationManager,
	type AnnotationUpdateEmitter,
	createAnnotationManager,
} from '@atlaskit/editor-common/annotation';
import { AnnotationUpdateEvent } from '@atlaskit/editor-common/types/emitter';
import type {
	InlineCommentSelectionComponentProps,
	InlineCommentViewComponentProps,
	InlineCommentHoverComponentProps,
	AnnotationByMatches,
	AnnotationActionResult,
} from '@atlaskit/editor-common/types/annotation';
import type { AddMarkStep } from '@atlaskit/editor-prosemirror/transform';
import type { JSONDocNode } from '@atlaskit/editor-json-transformer/types';
import { AnnotationTypes } from '@atlaskit/adf-schema/annotation';

import { CommentCreationLocation } from '@confluence/inline-comments-queries/entry-points/CreateInlineCommentMutation.ts';
import { usePageClass } from '@confluence/comments-hooks/entry-points/usePageClass.ts';
import { getRendererAnnotationEventEmitter } from '@confluence/annotation-event-emitter/entry-points/getRendererAnnotationEventEmitter.ts';
import {
	selectInlineComment,
	selectInlineCommentCompoundExperience,
} from '@confluence/experience-tracker/entry-points/selectInlineCommentCompoundExperience.ts';
import { createInlineCommentCompoundExperience } from '@confluence/experience-tracker/entry-points/createInlineCommentCompoundExperience.ts';
import type { CreateInlineCommentCompoundExperienceEntryPoint } from '@confluence/experience-tracker/entry-points/createInlineCommentCompoundExperience.ts';
import { VIEW_INLINE_COMMENT_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component.ts';
import { fg } from '@confluence/feature-gating';
import { expVal } from '@confluence/feature-experiments';
import { usePageContentId } from '@confluence/page-context';
import { useRendererActions } from '@confluence/renderer-actions/entry-points/RendererActionsContext.ts';
import type { ConfluenceEdition } from '@confluence/session-data/entry-points/SiteInformationQuery.ts';

// Timeout for waiting for annotation element to appear in DOM before failing the experience
export const ANNOTATION_ELEMENT_WAIT_TIMEOUT_MS = 2000;

// Copying from @confluence/annotation-provider-inline-comments/src/renderer/ViewComponent.tsx for circular deps
type ViewComponentProps = {
	pageId: string;
	updateDocument?: (doc: JSONDocNode) => void;
	initialCommentData?: any;
};

// Copying from @confluence/annotation-provider-inline-comments/src/renderer/SelectionComponent.tsx for circular deps
type SelectionComponentProps = {
	pageId: string;
	updateDocument?: (doc: JSONDocNode) => void;
	isArchived?: boolean;
};

// Copying from @confluence/annotation-provider-inline-comments/src/renderer/HoverComponent.tsx for circular deps
type HoverComponentProps = {
	pageId: string;
	updateDocument?: (doc: JSONDocNode) => void;
	isArchived?: boolean;
};

// Copying from @confluence/annotation-provider-inline-comments/src/renderer/SelectionComponent.tsx for circular deps
export type SelectionOptions = AnnotationByMatches & {
	createdFrom: CommentCreationLocation;
	step?: AddMarkStep;
	inlineNodeTypes?: string[];
	targetNodeType?: string;
	pos?: number;
};

// Copying from @confluence/annotation-provider-inline-comments/src/editor/CreateComponent.tsx for circular deps
type EditorCreateComponentProps = {
	pageId: string;

	/**
	 * Method to close the comments panel if it is open while creating a new comment in the editor
	 */
	closeObjectSidebar?: () => void;
};

// Copying from @confluence/annotation-provider-inline-comments/src/editor/ViewComponent.tsx for circular deps
type EditorViewComponentProps = {
	pageId: string;
	editorActions?: EditorActions;
	reactionMarkerMap?: Record<string, string>;
};

export type AnnotationsContextType = {
	selectionProps: (InlineCommentSelectionComponentProps & SelectionComponentProps) | null;
	hoverProps: (InlineCommentHoverComponentProps & HoverComponentProps) | null;
	viewProps: (InlineCommentViewComponentProps & ViewComponentProps) | null;
	editorCreateProps: Omit<
		EditorInlineCommentCreateComponentProps & EditorCreateComponentProps,
		'isOffline'
	> | null;
	editorViewProps: Omit<
		EditorInlineCommentViewComponentProps & EditorViewComponentProps,
		'isOffline'
	> | null;
	showCreateComponent: boolean;
	showHighlightActions: boolean;
	selectionOptions: any;
	stepGenerationError: boolean;
	newlyCreatedAnnotationId?: string;
	/** UFO: 'create' when a comment was just saved; null = view. Reset when view sidebar unmounts. */
	commentSidebarContext: 'create' | null;
	isOffline: boolean;
	storedCommentData: any;
};

export type AnnotationDispatchContextType = {
	checkEditorCommentCloseGuard: () => Promise<boolean>;
	setEditorCommentCloseGuard: (guard: () => Promise<boolean>) => void;
	setSelectionProps: (
		selectionProps: (InlineCommentSelectionComponentProps & SelectionComponentProps) | null,
	) => void;
	setHoverProps: (
		hoverProps: (InlineCommentHoverComponentProps & HoverComponentProps) | null,
	) => void;
	setViewProps: (viewProps: (InlineCommentViewComponentProps & ViewComponentProps) | null) => void;
	setEditorCreateProps: (
		createProps: Omit<
			EditorInlineCommentCreateComponentProps & EditorCreateComponentProps,
			'isOffline'
		> | null,
	) => void;
	setEditorViewProps: (
		viewProps: Omit<
			EditorInlineCommentViewComponentProps & EditorViewComponentProps,
			'isOffline'
		> | null,
	) => void;
	setShowCreateComponent: (showCreateComponent: boolean) => void;
	setShowHighlightActions: (showHighlightActions: boolean) => void;
	setSelectionOptions: (selectionOptions: any) => void;
	setStepGenerationError: (stepGenerationError: boolean) => void;
	setSelectionOptionsForCreateEditor: (selectionOptions: {
		experienceAttributes?: {
			entryPoint: CreateInlineCommentCompoundExperienceEntryPoint;
			edition: ConfluenceEdition | null;
		};
	}) => void;
	setNewlyCreatedAnnotationId: (newlyCreatedAnnotationId?: string) => void;
	setCommentSidebarContext: (value: 'create' | null) => void;
	handleSaveSuccess: (annotationId: string, commentData?: any) => void;
	handleClose: () => void;
	setIsOffline: (isOffline: boolean) => void;
	setStoredCommentData: (commentData: any) => void;
	clearStoredCommentData: () => void;
	waitForAnnotationElementAndEmit: (annotationId: string, emitter: AnnotationUpdateEmitter) => void;
	applyDraftModeRef: React.RefObject<((opts: any) => boolean) | null>;
};

export const AnnotationsContext: React.Context<AnnotationsContextType> =
	createContext<AnnotationsContextType>({
		selectionProps: null,
		hoverProps: null,
		viewProps: null,
		editorCreateProps: null,
		editorViewProps: null,
		showCreateComponent: false,
		showHighlightActions: false,
		selectionOptions: undefined,
		stepGenerationError: false,
		newlyCreatedAnnotationId: undefined,
		commentSidebarContext: null,
		isOffline: false,
		storedCommentData: null,
	});
AnnotationsContext.displayName = 'AnnotationsContext';

export const AnnotationsOfflineContext: React.Context<boolean> =
	createContext<AnnotationsContextType['isOffline']>(false);
AnnotationsOfflineContext.displayName = 'AnnotationsOfflineContext';

const ConditionalAnnotationsOfflineContextProvider = ({
	children,
	value,
}: {
	children: ReactNode;
	value: AnnotationsContextType['isOffline'];
}) => {
	if (expVal('platform_editor_offline_editing_web', 'isEnabled', false)) {
		return (
			<AnnotationsOfflineContext.Provider value={value}>
				{children}
			</AnnotationsOfflineContext.Provider>
		);
	} else {
		return <>{children}</>;
	}
};

export const AnnotationsDispatchContext: React.Context<AnnotationDispatchContextType> =
	createContext<AnnotationDispatchContextType>({
		checkEditorCommentCloseGuard: () => Promise.resolve(true),
		setEditorCommentCloseGuard: () => {},
		setSelectionProps: () => {},
		setHoverProps: () => {},
		setViewProps: () => {},
		setEditorCreateProps: () => {},
		setEditorViewProps: () => {},
		setShowCreateComponent: () => {},
		setShowHighlightActions: () => {},
		setSelectionOptions: () => {},
		setStepGenerationError: () => {},
		setSelectionOptionsForCreateEditor: () => {},
		setNewlyCreatedAnnotationId: () => {},
		setCommentSidebarContext: () => {},
		handleSaveSuccess: () => {},
		handleClose: () => {},
		setIsOffline: () => {},
		setStoredCommentData: () => {},
		clearStoredCommentData: () => {},
		waitForAnnotationElementAndEmit: () => {},
		applyDraftModeRef: { current: null },
	});
AnnotationsDispatchContext.displayName = 'AnnotationDispatchContext';

export const AnnotationManagerContext: React.Context<AnnotationManager | undefined> = createContext<
	AnnotationManager | undefined
>(undefined);
AnnotationManagerContext.displayName = 'AnnotationManagerContext';

type AnnotationProviderProps = {
	children: JSX.Element;
};

export const AnnotationsProvider: FC<AnnotationProviderProps> = ({ children }) => {
	const editorCommentCloseGuardRef = useRef<() => Promise<boolean>>(() => Promise.resolve(true));
	const setEditorCommentCloseGuard = useCallback((guard: () => Promise<boolean>) => {
		editorCommentCloseGuardRef.current = guard;
	}, []);
	const checkEditorCommentCloseGuard = useCallback(() => editorCommentCloseGuardRef.current(), []);
	const [selectionProps, setSelectionProps] = useState<any>(null);
	const selectionPropsRef = useRef<any>(null);
	/** Stable ref to applyDraftMode — never cleared, so nudge click can call it synchronously
	 *  without waiting for the 100ms selectionchange debounce. */
	const applyDraftModeRef = useRef<((opts: any) => boolean) | null>(null);

	// Keep refs in sync with state — useEffect so it never runs during render
	useEffect(() => {
		selectionPropsRef.current = selectionProps;
		if (selectionProps?.applyDraftMode) {
			applyDraftModeRef.current = selectionProps.applyDraftMode;
		}
	}, [selectionProps]);

	const [hoverProps, setHoverProps] = useState<any>(null);
	const [viewProps, setViewProps] = useState<any>(null);
	const [editorCreateProps, setEditorCreateProps] = useState<any>(null);
	const [editorViewProps, setEditorViewProps] = useState<any>(null);
	const [showCreateComponent, setShowCreateComponent] = useState<boolean>(false);
	const [showHighlightActions, setShowHighlightActions] = useState(false);
	const [selectionOptions, setSelectionOptions] = useState<SelectionOptions | undefined>();
	const [stepGenerationError, setStepGenerationError] = useState(false);

	const [newlyCreatedAnnotationId, setNewlyCreatedAnnotationId] = useState<string | undefined>();
	const [commentSidebarContext, setCommentSidebarContext] = useState<'create' | null>(null);
	const [prevContentId, setPrevContentId] = useState<string | null>(null);
	const [contentId] = usePageContentId();
	const pageClass = usePageClass();
	const experienceTracker = useContext(ExperienceTrackerContext);
	const annotationManager = useMemo(
		() => (fg('platform_editor_comments_api_manager') ? createAnnotationManager() : undefined),
		[],
	);
	// This is effectively used with editorCreateProps and editorViewProps but we're tracking it independently as it doesn't really tie into the same lifecycle events
	const [isOffline, setIsOffline] = useState(false);
	// Stored comment data for direct data flow
	const [storedCommentData, setStoredCommentData] = useState<any>(null);

	// Track active MutationObserver and timeout for cleanup on unmount
	const activeObserverRef = useRef<MutationObserver | null>(null);
	const activeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	/**
	 * Waits for an annotation element to appear in the DOM using MutationObserver,
	 * then emits annotation selection events. If element doesn't appear within 2 seconds,
	 * fails the VIEW_INLINE_COMMENT_EXPERIENCE.
	 */
	const waitForAnnotationElementAndEmit = useCallback(
		(annotationId: string, emitter: AnnotationUpdateEmitter) => {
			// Clean up any existing observer/timeout before creating new ones
			if (activeObserverRef.current) {
				activeObserverRef.current.disconnect();
			}
			if (activeTimeoutRef.current) {
				clearTimeout(activeTimeoutRef.current);
			}

			// Use MutationObserver to wait for element to appear in DOM
			activeObserverRef.current = new MutationObserver(() => {
				// eslint-disable-next-line check-react-ssr-usage/guard-browser-globals
				const element = document.getElementById(annotationId);
				if (element) {
					if (activeObserverRef.current) activeObserverRef.current.disconnect();
					if (activeTimeoutRef.current) clearTimeout(activeTimeoutRef.current);
					activeObserverRef.current = null;
					activeTimeoutRef.current = null;

					// Emit annotation events and check if listeners exist
					if (
						emitter.emit(AnnotationUpdateEvent.ON_ANNOTATION_CLICK, {
							annotationIds: [annotationId],
							eventTarget: element,
						})
					) {
						emitter.emit(AnnotationUpdateEvent.SET_ANNOTATION_FOCUS, {
							annotationId,
						});
						setNewlyCreatedAnnotationId(annotationId);
					} else {
						// No listeners registered - fail the experience
						experienceTracker.fail({
							name: VIEW_INLINE_COMMENT_EXPERIENCE,
							error: new Error('Failed to emit annotation click'),
						});
					}
				}
			});

			// Observe the renderer wrapper for child additions (fallback to document.body for safety)
			const rendererWrapper =
				// eslint-disable-next-line check-react-ssr-usage/guard-browser-globals
				document.querySelector('#content .ak-renderer-wrapper') || document.body;
			activeObserverRef.current.observe(rendererWrapper, {
				childList: true,
				subtree: true,
			});

			// Fallback: If element doesn't appear within timeout, fail the experience
			// This almost always happens due to super slow re-renders (slow client on large page)
			activeTimeoutRef.current = setTimeout(() => {
				if (activeObserverRef.current) activeObserverRef.current.disconnect();
				activeObserverRef.current = null;
				activeTimeoutRef.current = null;
				experienceTracker.fail({
					name: VIEW_INLINE_COMMENT_EXPERIENCE,
					error: new Error(
						`Annotation element not found in DOM after ${ANNOTATION_ELEMENT_WAIT_TIMEOUT_MS}ms`,
					),
				});
			}, ANNOTATION_ELEMENT_WAIT_TIMEOUT_MS);
		},
		[experienceTracker, setNewlyCreatedAnnotationId],
	);

	// Cleanup observer and timeout on unmount
	useEffect(() => {
		return () => {
			if (activeObserverRef.current) {
				activeObserverRef.current.disconnect();
				activeObserverRef.current = null;
			}
			if (activeTimeoutRef.current) {
				clearTimeout(activeTimeoutRef.current);
				activeTimeoutRef.current = null;
			}
		};
	}, []);
	// Get rendererActions for renderer mode (always call hook, but only use when feature gate is enabled)
	const rendererActionsHook = useRendererActions();
	const rendererActions = rendererActionsHook?.rendererActions;

	useEffect(() => {
		const emitter = getRendererAnnotationEventEmitter();
		function handleAnnotationFocus({ annotationId }: { annotationId: string }) {
			// The fact annotations is an array here is meaningless at this time - Confluence
			// always picks the top annotation
			const focusedAnnotation = annotationId;
			if (!focusedAnnotation) {
				// If there is no running experience -- then this is a noop.
				selectInlineComment.debug('Renderer emitted event indicating no focused annotation.');
				return;
			}

			selectInlineCommentCompoundExperience.selectAnnotation.complete({
				pageClass,
				entryPoint: 'unknown:RendererFocus',
				annotationId: focusedAnnotation,
			});
		}
		// Fired when an annotation is focused (ie. comment navigation, comment click)
		emitter.on(AnnotationUpdateEvent.SET_ANNOTATION_FOCUS, handleAnnotationFocus);
		return () => {
			emitter.off(AnnotationUpdateEvent.SET_ANNOTATION_FOCUS, handleAnnotationFocus);
		};
	}, [pageClass]);

	const resetProviderState = useCallback(() => {
		setSelectionProps(null);
		setHoverProps(null);
		setViewProps(null);
		setEditorCreateProps(null);
		setEditorViewProps(null);
		setShowCreateComponent(false);
		setShowHighlightActions(false);
		setSelectionOptions(undefined);
		setStepGenerationError(false);
		setNewlyCreatedAnnotationId(undefined);
		setCommentSidebarContext(null);
		setStoredCommentData(null);
	}, []);

	// When the content ID changes, reset the provider state
	useEffect(() => {
		if (!contentId) {
			return;
		}

		if (prevContentId && contentId !== prevContentId) {
			resetProviderState();
			// Deselect any currently selected annotations
			getRendererAnnotationEventEmitter().emit(AnnotationUpdateEvent.DESELECT_ANNOTATIONS);
		}
		setPrevContentId(contentId);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [contentId]);

	// Stable ref for selectionOptions — updated synchronously alongside every setSelectionOptions call
	// so handleSaveSuccess always reads the latest step even when its useCallback closure is stale.
	const selectionOptionsRef = useRef<SelectionOptions | undefined>(undefined);

	const handleClose = useCallback(() => {
		setShowCreateComponent(false);
		setStepGenerationError(false);
		if (expVal('confluence_inline_comments_fix_stale_selection', 'isEnabled', false)) {
			selectionOptionsRef.current = undefined;
		}
		setSelectionOptions(undefined);

		if (annotationManager) {
			annotationManager.clearDraft();
		} else {
			selectionProps && selectionProps.onClose();
		}

		setSelectionProps(null);
	}, [
		setShowCreateComponent,
		setStepGenerationError,
		setSelectionOptions,
		selectionProps,
		annotationManager,
	]);

	/*
	 * NOTE: This function will not be calling setShowCreateComponent because it's
	 *       consumers will not be refreshing themselves with new selectionOptions.
	 *       It will now be the responsibility of the consumers of this function to call
	 *       setShowCreateComponent(true). See SelectionComponent.tsx (~line 132)
	 */
	const setSelectionOptionsForCreateEditor = useCallback(
		({
			experienceAttributes,
		}: {
			experienceAttributes?: {
				entryPoint: CreateInlineCommentCompoundExperienceEntryPoint;
				edition: ConfluenceEdition | null;
			};
		} = {}) => {
			setShowCreateComponent(false); // Hide an existing create component if it exists

			const annotationId = uuid();

			// Narrow pageClass to 'renderer' | 'editor' for the experience attributes
			const pageClassAttr: 'renderer' | 'editor' = pageClass === 'renderer' ? 'renderer' : 'editor';

			createInlineCommentCompoundExperience.start({
				attributes: {
					pageClass: pageClassAttr,
					commentType: 'inline',
					annotationId,
					...experienceAttributes,
				},
			});

			createInlineCommentCompoundExperience.initExperience.start();

			let result: ActionResult | undefined = undefined;
			if (annotationManager) {
				const startDraftResult = annotationManager.startDraft();
				if (startDraftResult.success) {
					result = startDraftResult.actionResult;
				}
			} else {
				/* selectionPropsRef fallback: selectionProps (React state) may be null immediately
				   after a sidebar close due to a render-cycle gap, while the ref holds the last-known
				   value. Prefer the ref (updated synchronously) so nudge click always gets the freshest
				   value. For experiment-OFF callers, selectionProps is non-null so ref vs state is moot. */
				const currentSelectionProps = selectionPropsRef.current ?? selectionProps;
				if (!currentSelectionProps) {
					// selectionProps is not defined. This may occur if a comment sidebar box was just closed, which
					// unsets selectionProps. In this case, we treat this as no-op, and unselect any annotation.
					getRendererAnnotationEventEmitter().emit(AnnotationUpdateEvent.DESELECT_ANNOTATIONS);
					// We soft-fail create-inline-comment/init experience here to track how often this occurs.
					createInlineCommentCompoundExperience.initExperience.softFail(
						new Error('Attempted to create inline comment when no selection was present'),
					);
					return;
				}
				// Reset ref before applyDraftMode so handleSaveSuccess reads the fresh step (COMMENTS-6594).
				if (expVal('confluence_inline_comments_fix_stale_selection', 'isEnabled', false)) {
					selectionOptionsRef.current = undefined;
				}
				result = currentSelectionProps.applyDraftMode({
					annotationId,
					keepNativeSelection: false,
				});
			}

			const ancestorNodeTypes = result?.ancestorNodeTypes ?? [];
			const alphabeticalAncestorNodeTypes = [...ancestorNodeTypes].sort();

			createInlineCommentCompoundExperience.addCommonAttributes({
				inlineNodeTypes: result?.inlineNodeTypes ?? [],
				ancestorNodeTypes,
				alphabeticalAncestorNodeTypes,
				// @ts-ignore
				initialPosition: [result?.step?.from, result?.step?.to],
			});

			if (result) {
				const newSelectionOptions = {
					step: result.step as AddMarkStep,
					inlineNodeTypes: result.inlineNodeTypes,
					originalSelection: result.originalSelection,
					numMatches: result.numMatches,
					matchIndex: result.matchIndex,
					createdFrom: CommentCreationLocation.RENDERER,
					pos: result.pos,
					targetNodeType: result.targetNodeType,
				};
				// Update ref synchronously BEFORE setSelectionOptions so handleSaveSuccess
				// always reads the latest step even if its closure captured a stale value.
				selectionOptionsRef.current = newSelectionOptions;
				setSelectionOptions(newSelectionOptions);

				// Tell the renderer to close any currently-open comments
				getRendererAnnotationEventEmitter().emit(AnnotationUpdateEvent.DESELECT_ANNOTATIONS);

				setShowHighlightActions(false);
			} else {
				setStepGenerationError(true);

				createInlineCommentCompoundExperience.initExperience.fail(
					new Error('Failed to generate step'),
				);
			}
		},
		[
			selectionProps,
			setShowCreateComponent,
			setShowHighlightActions,
			setStepGenerationError,
			annotationManager,
			pageClass,
		],
	);

	const handleSaveSuccess = useCallback(
		(annotationId: string, commentData?: any) => {
			setStepGenerationError(false);
			setCommentSidebarContext('create');

			// Set stored comment data for direct data flow when experiment is enabled
			if (commentData) {
				setStoredCommentData(commentData);
			}

			let result: AnnotationActionResult = false;

			if (annotationManager) {
				const result = annotationManager.applyDraft(annotationId);
				if (result.success && !!result.actionResult?.doc) {
					selectionProps.updateDocument && selectionProps.updateDocument(result.actionResult.doc);
					setNewlyCreatedAnnotationId(annotationId);
				} else {
					throw new Error('Error ocurred when applying the step to the active document');
				}
			} else {
				// When the experiment is on, read from ref to get the latest selectionOptions regardless of
				// closure staleness — the create panel may hold an old handleSaveSuccess reference from before
				// setSelectionOptions was called for the new selection, causing the stale step to be used.
				const currentSelectionOptions =
					selectionOptions &&
					expVal('confluence_inline_comments_fix_stale_selection', 'isEnabled', false)
						? (selectionOptionsRef.current ?? selectionOptions)
						: selectionOptions;
				// Renderer mode: Try using rendererActions.applyAnnotation() first (similar to editor's annotationManager.applyDraft())
				// This provides more reliable annotation application, matching the editor implementation
				if (rendererActions && currentSelectionOptions?.step) {
					const step = currentSelectionOptions.step;
					// Extract position from step - use pos if available, otherwise use from/to
					const position = currentSelectionOptions.pos
						? { to: currentSelectionOptions.pos, from: currentSelectionOptions.pos }
						: { to: step.to, from: step.from };

					const annotationResult = rendererActions.applyAnnotation(position, {
						annotationId,
						annotationType: AnnotationTypes.INLINE_COMMENT,
					});

					if (annotationResult && annotationResult.doc) {
						const emitter = getRendererAnnotationEventEmitter();

						if (!storedCommentData) {
							selectionProps.removeDraftMode();
						}
						selectionProps.updateDocument && selectionProps.updateDocument(annotationResult.doc);

						waitForAnnotationElementAndEmit(annotationId, emitter);
					} else {
						throw new Error('Error ocurred when applying the step to the active document');
					}
				} else {
					// Fallback to original onCreate method
					// Since we generated the uuid ourselves, we need to replace the document generated with
					// the annotationId that we received from the backend so things line up correctly
					result = selectionProps.onCreate(annotationId);

					if (result) {
						const emitter = getRendererAnnotationEventEmitter();

						if (!storedCommentData) {
							selectionProps.removeDraftMode();
						}
						selectionProps.updateDocument && selectionProps.updateDocument(result.doc);

						waitForAnnotationElementAndEmit(annotationId, emitter);
					} else {
						throw new Error('Error ocurred when applying the step to the active document');
					}
				}
			}
		},
		[
			annotationManager,
			setStepGenerationError,
			setNewlyCreatedAnnotationId,
			selectionProps,
			setStoredCommentData,
			storedCommentData,
			rendererActions,
			selectionOptions,
			waitForAnnotationElementAndEmit,
		],
	);

	const providerValue = useMemo(
		() => ({
			selectionProps,
			hoverProps,
			viewProps,
			editorCreateProps,
			editorViewProps,
			showCreateComponent,
			showHighlightActions,
			selectionOptions,
			stepGenerationError,
			newlyCreatedAnnotationId,
			commentSidebarContext,
			isOffline,
			storedCommentData,
		}),
		[
			selectionProps,
			hoverProps,
			viewProps,
			editorCreateProps,
			editorViewProps,
			showCreateComponent,
			showHighlightActions,
			selectionOptions,
			stepGenerationError,
			newlyCreatedAnnotationId,
			commentSidebarContext,
			isOffline,
			storedCommentData,
		],
	);

	const dispatchProviderValue = useMemo(
		() => ({
			checkEditorCommentCloseGuard,
			setEditorCommentCloseGuard,
			setSelectionProps,
			setHoverProps,
			setViewProps,
			setEditorCreateProps,
			setEditorViewProps,
			setShowCreateComponent,
			setShowHighlightActions,
			setSelectionOptions,
			setStepGenerationError,
			setSelectionOptionsForCreateEditor,
			setNewlyCreatedAnnotationId,
			setCommentSidebarContext,
			handleSaveSuccess,
			handleClose,
			setIsOffline,
			// Stored comment data management functions
			setStoredCommentData: (commentData: any) => setStoredCommentData(commentData ?? null),
			clearStoredCommentData: () => setStoredCommentData(null),
			waitForAnnotationElementAndEmit,
			applyDraftModeRef,
		}),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[
			checkEditorCommentCloseGuard,
			setEditorCommentCloseGuard,
			setSelectionProps,
			setHoverProps,
			setViewProps,
			setEditorCreateProps,
			setEditorViewProps,
			setShowCreateComponent,
			setShowHighlightActions,
			setSelectionOptions,
			setStepGenerationError,
			setSelectionOptionsForCreateEditor,
			setNewlyCreatedAnnotationId,
			setCommentSidebarContext,
			handleSaveSuccess,
			handleClose,
			setIsOffline,
			// Stored comment data management functions
			setStoredCommentData,
			waitForAnnotationElementAndEmit,
		],
	);

	return (
		<AnnotationManagerContext.Provider value={annotationManager}>
			<AnnotationsContext.Provider value={providerValue}>
				<AnnotationsDispatchContext.Provider value={dispatchProviderValue}>
					<ConditionalAnnotationsOfflineContextProvider value={isOffline}>
						{children}
					</ConditionalAnnotationsOfflineContextProvider>
				</AnnotationsDispatchContext.Provider>
			</AnnotationsContext.Provider>
		</AnnotationManagerContext.Provider>
	);
};

export const useAnnotations = (): AnnotationsContextType => {
	return useContext(AnnotationsContext);
};

export const useAnnotationsDispatch = (): AnnotationDispatchContextType => {
	return useContext(AnnotationsDispatchContext);
};

export const useAnnotationManager = (): AnnotationManager | undefined => {
	return useContext(AnnotationManagerContext);
};

// Provide direct access to offline state so that it can be tracked by annotation UI elements that don't need to depend on the rest of the context
export const useAnnotationsIsOffline = (): boolean => {
	return useContext(AnnotationsOfflineContext);
};
